import cn from 'classnames';
import useIsClient from 'hooks/common/use-is-client';
import useMobile from 'hooks/common/use-mobile';
import dynamic from 'next/dynamic';
import NextImage from "next/legacy/image";
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { contentful } from 'utils/image-loader';
import { ISlide } from '../../../../../global';
import Image from '../../components/image';

const RichTitle = dynamic(
  () => import('modules/content-cms/components/rich-title')
);

interface ConditionalAnchorPropTypes {
  children: React.ReactNode;
  href?: string;
}

const ConditionalAnchor: React.FC<ConditionalAnchorPropTypes> = ({
  children,
  href,
}) => (href ? <a href={href}>{children}</a> : <>{children}</>);

interface PropTypes {
  slide: ISlide;
  priority?: boolean;
  nutritionGuidanceEnabled?: boolean;
}

const ReadableSlide: React.FC<PropTypes> = ({
  slide: {
    fields: {
      title,
      subTitle,
      backgroundImage,
      backgroundImageMobile,
      link,
      ctaLabel,
      uspSlide,
      ctaCustomBackgroundColor,
      h1Title,
      h1TitleRibbonColour,
      testBackgroundImage,
      testBackgroundImageMobile,
    },
  },
  priority,
}) => {
  const _backgroundImage = testBackgroundImage || backgroundImage;
  const _backgroundImageMobile =
    testBackgroundImageMobile || backgroundImageMobile;

  const { isClient } = useIsClient();
  const isMobile = useMobile();
  return (
    <ConditionalAnchor href={link}>
      <div className="relative w-full">
        <div className="relative">
          {_backgroundImage && (
            <div
              className={cn(
                'relative hidden h-88 w-full md:block',
                uspSlide
                  ? 'bg-contain bg-center bg-no-repeat'
                  : 'bg-cover bg-right'
              )}
            >
              <NextImage
                priority={priority}
                placeholder="empty"
                src={`${_backgroundImage.fields.file.url}`}
                objectFit="cover"
                objectPosition="center"
                layout="fill"
                loader={contentful}
                className={cn(
                  'relative z-10 hidden h-88 w-full md:block',
                  uspSlide
                    ? 'bg-contain bg-center bg-no-repeat'
                    : 'bg-cover bg-right'
                )}
              />
            </div>
          )}
          {_backgroundImageMobile && (
            <div className="block w-full md:hidden">
              <Image
                {..._backgroundImageMobile}
                className="relative z-10 w-full"
              />
            </div>
          )}
        </div>

        {h1Title && (
          <div
            className="absolute top-[5%] left-[17%] z-30 lg:top-[50%] lg:left-[30%]"
            style={{
              transform:
                isClient && isMobile
                  ? 'translate(-17%, -5%)'
                  : 'translate(-30%, -50%)',
            }}
          >
            <h1 className="font-mindset text-other-white text-[40px] leading-[1.2em] lg:text-[70px]">
              <RichTitle text={h1Title} />
            </h1>
          </div>
        )}

        {!h1Title && title && subTitle && (
          <div className="z-30 -mt-12 w-full md:absolute md:top-0 md:mt-0 md:flex md:h-88 md:items-center">
            <div className="relative w-full">
              <div className="container">
                <div className="bg-other-white relative z-30 rounded-xs px-5 py-5 text-center md:w-1/2 md:max-w-lg md:rounded-lg md:px-6 md:py-6 md:text-left">
                  <h2 className="mindset-24 lg:mindset-40 text-primary-main">
                    {title}
                  </h2>
                  <p className="text-grey-700 pt-2 text-sm md:pt-1 md:text-base">
                    {subTitle}
                  </p>
                  {link && (
                    <button
                      className={cn(
                        'bg-brand-cta text-other-white hover:bg-brand-cta/90 mt-6 inline-block rounded-sm px-2 py-1 font-black tracking-wider uppercase md:mt-3 md:px-6 md:pt-3 md:pb-2',
                        ctaCustomBackgroundColor ? 'text-md' : 'text-xs'
                      )}
                      style={
                        (ctaCustomBackgroundColor && {
                          backgroundColor: `${ctaCustomBackgroundColor}`,
                        }) ||
                        {}
                      }
                    >
                      {ctaLabel || <FormattedMessage id="cdp:findoutmore" />}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ConditionalAnchor>
  );
};

export default ReadableSlide;
